/**
 * What the user sees right now, as text, for 馍馍 (AssistantContext.screen): the open dialog's text (a settings page with each
 * setting's name and explanation, a form being filled in), and every field, switch and choice with its current value — typed but not
 * saved yet included — and which one has the focus. Passwords and keys are never included, nor 馍馍's own chat.
 */
const MAX = 8000;

/** Not part of what the user is asking about: the chat itself, the cat, hidden things. */
const SKIP = '.assistant, .window-cat, .cat-bed, [aria-hidden="true"], script, style, template';

function visible(el: Element): boolean {
  const h = el as HTMLElement;
  if (h.closest(SKIP)) return false;
  if (h.hidden) return false;
  const style = getComputedStyle(h);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const r = h.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function secret(el: HTMLInputElement): boolean {
  const name = `${el.name} ${el.id} ${el.autocomplete} ${el.getAttribute('aria-label') ?? ''} ${el.placeholder}`.toLowerCase();
  return el.type === 'password' || el.hasAttribute('data-private') || /password|passwd|secret|token|api.?key|one-time-code|密码|密钥/.test(name); // i18n-ok field names, not text
}

/** The name a field goes by: its label, aria-label, placeholder or the text just before it. */
function labelOf(el: HTMLElement): string {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const id = el.id;
  const byFor = id ? document.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
  if (byFor?.textContent?.trim()) return byFor.textContent.trim();
  const wrapping = el.closest('label');
  if (wrapping?.textContent?.trim()) return wrapping.textContent.trim();
  const ph = (el as HTMLInputElement).placeholder;
  if (ph) return ph;
  const row = el.closest('.settings__row, .field, .row, li, tr');
  return row?.textContent?.trim().slice(0, 80) ?? el.tagName.toLowerCase();
}

function clip(text: string, max: number): string {
  const t = text.replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

export function uiSnapshot(): string {
  const parts: string[] = [];
  // An open dialog (settings, a form) is what the user is looking at: its text as they read it
  const dialogs = [...document.querySelectorAll('[role="dialog"]')].filter(visible);
  for (const d of dialogs) {
    const title = d.querySelector('h2, h1, [id$="-title"]')?.textContent?.trim();
    parts.push(`[Open dialog${title ? `: ${title}` : ''}]\n${clip((d as HTMLElement).innerText, 4000)}`);
  }
  // Every field and choice in view, with its value as it is now (not yet saved, maybe)
  const fields: string[] = [];
  const focused = document.activeElement;
  for (const el of document.querySelectorAll<HTMLElement>('input, textarea, select, [role="switch"], [role="checkbox"], [role="radio"], [role="tab"], [aria-pressed]')) {
    if (!visible(el)) continue;
    const mark = el === focused ? '  ← cursor here' : '';
    if (el instanceof HTMLInputElement) {
      if (el.type === 'hidden' || el.type === 'file' || el.type === 'submit' || el.type === 'button') continue;
      if (secret(el)) {
        fields.push(`${labelOf(el)}: (hidden: ${el.value ? 'filled in' : 'empty'})${mark}`);
        continue;
      }
      if (el.type === 'checkbox' || el.type === 'radio') fields.push(`${labelOf(el)}: ${el.checked ? 'on' : 'off'}${mark}`);
      else fields.push(`${labelOf(el)}: "${clip(el.value, 500)}"${mark}`);
    } else if (el instanceof HTMLTextAreaElement) {
      fields.push(`${labelOf(el)}: "${clip(el.value, 3000)}"${mark}`);
    } else if (el instanceof HTMLSelectElement) {
      fields.push(`${labelOf(el)}: ${el.selectedOptions[0]?.textContent?.trim() ?? el.value} (choices: ${[...el.options].map((o) => o.textContent?.trim()).join(' / ')})${mark}`);
    } else {
      const role = el.getAttribute('role');
      const state = el.getAttribute('aria-checked') ?? el.getAttribute('aria-pressed') ?? el.getAttribute('aria-selected');
      if (state === null) continue;
      const name = clip(el.getAttribute('aria-label') ?? el.textContent ?? '', 80);
      if (!name) continue;
      fields.push(`${role === 'tab' ? 'tab' : 'choice'} "${name}": ${state === 'true' ? (role === 'tab' ? 'selected' : 'on') : 'off'}${mark}`);
    }
  }
  if (fields.length) parts.push(`[Fields and choices on screen]\n${fields.join('\n')}`);
  return clip(parts.join('\n\n'), MAX);
}
